import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cN from 'classnames';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';
import { CollapseList, ICollapseListProps } from '@lion-design/core';
import { GroupAllGuaranteed } from '@components/DetailPage';
import { RootState } from '@Redux/detail/store';
import {
    setGroupDetail,
    setGroupAllCalendar,
    setCalendarGroupsMonth,
    setStationInfo,
    setPricesInfo,
    setPricesInfoSUCCESS,
    setFlightInfo,
} from '@Redux/detail/Group/actions';
import { setItineraries, setOptionalInfo, setNoticeinfo } from '@Redux/detail/Itineraries/actions';
import { I_CalendarGroup } from '@Redux/detail/Group/interfaces';
import { ArrowLeft } from '@components/Common/Icons';
import Spinner from '@components/Common/Spinner';
import { changeDetailHistory } from '@utils/utils';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

let once = true;

const GroupAllList: React.FC = () => {
    const { preferairlines } = document.getElementById('detailPagePara')!.dataset!;
    const { Schedules, GroupInfo, currentTourID, IsPreview, isLoadingTours } = useSelector(
        (state: RootState) => state.groupReducer
    );
    const { CalendarGroups, chooseDateM } = useSelector(
        (state: RootState) => state.calendarInfoReducer
    );

    const { isLoadingCalendar } = CalendarGroups;
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

    const { t } = useTranslation();

    const dispatch = useDispatch();

    useEffect(() => {
        let flag = false;
        let data;
        // 只讓程式初次載入時跑進來一次，不需要多餘的進入
        if (CalendarGroups.groups.length > 0 && once && !IsPreview) {
            once = false;
            // 當進入頁面後，左側產品圖示沒有配對到右側產品，則顯示右側產品的第一個
            CalendarGroups.groups?.map((group: I_CalendarGroup) => {
                if (group.ID === GroupInfo.GroupID) {
                    flag = true;
                }
            });
            if (!flag) {
                data = CalendarGroups.groups[0];
                clickGroup(data);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 以行程 API (tourinfojson) 中得知最先&最後出發日期，得知最小&最大月份
    const isMaxMonth = (function compareScheduleMax() {
        const MaxMonth = dayjs(Schedules?.AllMaxGoDate).format('YYYY-MM');
        return !Schedules?.AllMaxGoDate
            ? true
            : dayjs(CalendarGroups.month).isSameOrAfter(MaxMonth);
    })();

    const isMinMonth = (function compareScheduleMin() {
        const MinMonth = dayjs(Schedules?.AllMinGoDate).format('YYYY-MM');
        return !Schedules?.AllMinGoDate
            ? true
            : dayjs(CalendarGroups.month).isSameOrBefore(MinMonth);
    })();

    function clickGroup(data: I_CalendarGroup) {
        // 重複點擊同一團號日期時，則不需更新資料
        if (data.ID === GroupInfo.GroupID) return;

        const reqBody = {
            GroupID: data.ID,
            TourSource: GroupInfo.TourSource,
        };
        const FlightInfoReqBody = {
            GroupID: data.ID,
            NormGroupID: data.NormGroupID,
            GoDate: dayjs(data.Date).format('YYYY-MM-DD'),
            TourSource: GroupInfo.TourSource,
        };

        // 切換不同行程安排時，需重新取得每日行程資料
        if (data.TourID !== GroupInfo.TourID) {
            const itinerariesReqbody = {
                TourID: data.TourID,
                IsPreview: IsPreview,
                TravelType: GroupInfo.TravelType,
                TourSource: GroupInfo.TourSource,
            };
            dispatch(setItineraries(itinerariesReqbody));
        }

        dispatch(setGroupDetail(reqBody));
        dispatch(setStationInfo(reqBody));
        dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
        dispatch(setPricesInfoSUCCESS(null)); // 為了避免點擊"費用說明"時,資料尚未更新即開啟光箱造成 bug
        dispatch(setPricesInfo(reqBody));
        dispatch(setFlightInfo(FlightInfoReqBody));
        changeDetailHistory({ NormGroupID: data.NormGroupID, GroupID: data.ID });
    }

    function clickPrevOrNext(condition: 'prev' | 'next') {
        const month =
            condition === 'prev'
                ? dayjs(CalendarGroups.month).subtract(1, 'month').format('YYYY-MM')
                : dayjs(CalendarGroups.month).add(1, 'month').format('YYYY-MM');
        const [GoDateStart, GoDateEnd] = getMonthStartAndEnd(month);

        const reqBody = {
            NormGroupID: GroupInfo.NormGroupID,
            GoDateStart,
            GoDateEnd,
            TourID: currentTourID,
            preferairlines,
            TourSource: GroupInfo.TourSource,
        };

        dispatch(setCalendarGroupsMonth(month));
        dispatch(setGroupAllCalendar(reqBody));
    }

    // 列表資料格式轉換
    const createGroupDataList = (group: I_CalendarGroup) => {
        const {
            WeekDay,
            Date,
            Guaranteed,
            CurrencyCode,
            IsVip,
            Price,
            Status,
            VipOrigPrice,
            TotalVacnacy,
        } = group;
        const dataSourceItem = {
            ...group,
            Date: `${Date.replace(/-/g, '/')} (${WeekDay})`,
            IsVip: IsVip && IsVipText,
            Guaranteed: Guaranteed && <GuaranteedTag />,
            MobileLinebreak: '',
            onClick: clickGroup,
            isActive: group.ID === GroupInfo.GroupID,
            Price: PriceContent(IsVip, Price, VipOrigPrice, CurrencyCode, Status, isMobile),
            TotalVacnacy: <TotalVacnacyContent TotalVacnacy={TotalVacnacy} />,
        };
        return dataSourceItem;
    };

    // pc完整月清單
    const dataSourceList = CalendarGroups.groups?.map(createGroupDataList);

    // pc月清單(只找成行)
    const guaranteedDataList = (CalendarGroups.groups || [])
        .filter((group: I_CalendarGroup) => group.Guaranteed === true)
        .map(createGroupDataList);

    // m版月曆對應出團清單
    const chooseDateDataList = (CalendarGroups.groups || [])
        .filter((group: I_CalendarGroup) => group.Date.replace(/\//g, '-') === chooseDateM)
        .map(createGroupDataList);

    // m版月曆對應出團清單(只找成行)
    const guaranteedDataListM = (CalendarGroups.groups || [])
        .filter(
            (group: I_CalendarGroup) =>
                group.Date.replace(/\//g, '-') === chooseDateM && group.Guaranteed === true
        )
        .map(createGroupDataList);

    const isGuaranteedChecked =
        !CalendarGroups.isGuaranteedDisabled && CalendarGroups.parmIsEnsureGroup;

    const dataSource =
        isMobile && chooseDateM
            ? isGuaranteedChecked
                ? guaranteedDataListM // m版月曆對應出團清單(只找成行)
                : chooseDateDataList // m版月曆對應出團清單
            : isGuaranteedChecked
            ? guaranteedDataList // pc月清單(只找成行)
            : dataSourceList; // pc完整月清單

    const titles: ICollapseListProps['titles'] = [
        {
            name: '出發日期',
            key: 'Date',
            size: '21%',
        },
        {
            name: '行程',
            key: 'ScheduleType',
            size: '6%',
        },
        {
            name: '',
            key: 'Guaranteed',
            size: '6%',
        },
        {
            name: '',
            key: 'IsVip',
            size: '14%',
        },
        {
            name: `${t('IsEnsureGroup')}狀態`,
            key: 'Status',
            size: '14%',
        },
        {
            name: '',
            key: 'MobileLinebreak',
            size: '0%',
        },
        {
            name: '可賣',
            key: 'AvailableVacancy',
            size: '9%',
        },
        {
            name: t('seats'),
            key: 'TotalVacnacy',
            size: '9%',
        },
        {
            name: '價格',
            key: 'Price',
            size: '21%',
        },
    ];

    return isMobile ? (
        <div className={cN(styles['listContainer'])}>
            <div className={cN(styles['listWrap'])}>
                {Schedules?.TourIDList && <span className={styles.mTitle}>選擇行程</span>}
                <GroupAllGuaranteed />
            </div>
            <div
                className={cN(styles.container, 'group_all_list', {
                    [`${styles.show}`]: CalendarGroups.isListMode,
                })}
            >
                {isLoadingTours || isLoadingCalendar ? (
                    <Spinner />
                ) : (
                    CalendarGroups.groups?.length > 0 && (
                        <CollapseList
                            className={styles.groups}
                            titles={titles}
                            dataSource={dataSource}
                        />
                    )
                )}
            </div>
        </div>
    ) : (
        <div className={cN(styles['listContainer'])}>
            {Schedules?.TourIDList && <span className={styles.mTitle}>選擇出發日期</span>}
            <div
                className={cN(styles.container, 'group_all_list', {
                    [`${styles.show}`]: CalendarGroups.isListMode,
                })}
            >
                <div className={styles.switcher}>
                    <span className={styles['switcher_text']}>
                        {dayjs(CalendarGroups.month).format('YYYY年M月')}
                    </span>
                    <GroupAllGuaranteed />
                    <span
                        className={`${styles['switcher_prev']} ${isMinMonth ? 'v-hide' : ''}`}
                        onClick={isMinMonth ? undefined : () => clickPrevOrNext('prev')}
                    >
                        <ArrowLeft fill="#f03742" />
                    </span>
                    <span
                        className={`${styles['switcher_next']} ${isMaxMonth ? 'v-hide' : ''}`}
                        onClick={isMaxMonth ? undefined : () => clickPrevOrNext('next')}
                    >
                        <ArrowLeft fill="#f03742" />
                    </span>
                </div>
                {isLoadingTours || isLoadingCalendar ? (
                    <Spinner />
                ) : CalendarGroups.groups?.length > 0 ? (
                    <CollapseList
                        className={styles.groups}
                        titles={titles}
                        dataSource={dataSource}
                    />
                ) : (
                    <div
                        className={cN(styles.thisMonthNoData, {
                            [`${styles.allNoneData}`]:
                                !Schedules?.AllMinGoDate && !Schedules?.AllMaxGoDate,
                        })}
                    />
                )}
            </div>
        </div>
    );
};

export default GroupAllList;

/*******************/

function PriceContent(
    isVip: boolean,
    price: string,
    vipPrice: number,
    currencyCode: string,
    status: string,
    isMobile: boolean
) {
    function changeprice(price: number | string) {
        return `${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
    return (
        <>
            {price && (
                <span className={`${styles.price} ${price !== '近期上架' ? styles.hasPrice : ''}`}>
                    {price !== '近期上架' ? currencyCode : ''}&ensp;
                    {isMobile ? price.replace(/起/g, '') : price}
                </span>
            )}
            {isVip && (
                <span className={styles.vipPrice}>
                    原價：{currencyCode || ''} {changeprice(vipPrice)}
                </span>
            )}
        </>
    );
}

function TotalVacnacyContent(props: { TotalVacnacy: number }) {
    const { t } = useTranslation();

    return (
        <>
            <span className={styles.label}>{t('seats')}</span>
            {props.TotalVacnacy}
        </>
    );
}

const IsVipText = <div className={styles.isVip}>會員限購</div>;

function GuaranteedTag() {
    const { t } = useTranslation();
    return <div className={styles.guaranteed}>{t('IsEnsureGroup')}</div>;
}

function getMonthStartAndEnd(day: string) {
    const start = dayjs(day).startOf('month').format('YYYY-MM-DD');
    const end = dayjs(day).endOf('month').format('YYYY-MM-DD');

    return [start, end];
}
